'use client';

/* ═══════════════════════════════════════════════════
   JacquardIconProvider — Icon registry overrides (ADR-025)

   Wraps a subtree with a merged icon registry so adopters
   can swap individual icon intents without forking the kit.

   Usage:
     <JacquardIconProvider iconOverrides={{ close: 'XCircle' }}>
       <App />
     </JacquardIconProvider>
   ═══════════════════════════════════════════════════ */

import { useMemo, type ReactNode } from 'react';
import { ICON_REGISTRY, type IconIntent } from '../../icons/registry';
import { JacquardIconContext } from '../../icons/context';

export type IconOverrides = Partial<Record<IconIntent, string>>;

export interface JacquardIconProviderProps {
  /** Partial overrides for the icon registry. Maps intent to Lucide component name. */
  iconOverrides: IconOverrides;
  children: ReactNode;
}

export function JacquardIconProvider({ iconOverrides, children }: JacquardIconProviderProps) {
  const registry = useMemo(
    () => ({ ...ICON_REGISTRY, ...iconOverrides }) as typeof ICON_REGISTRY,
    [iconOverrides],
  );

  return <JacquardIconContext.Provider value={registry}>{children}</JacquardIconContext.Provider>;
}
